import { RateInterval } from '__types__/enums';
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { FieldSpecOf } from 'v5/core/entity-schema/entitySchema.types';
import type { RateFormValue } from 'v5/core/entity-values/values.types';
import { formatMoneyAmount } from 'v5/core/money/formatMoney';
import { decimalSeparatorOf, parseMoneyInput } from 'v5/core/money/moneyInput';
import {
  isResourceRateInterval,
  RATE_FEES,
  RateFee,
  RESOURCE_RATE_INTERVALS
} from 'v5/core/rates/resourceRate';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from 'v5/design-system/Form';
import { Input2, TextArea2 } from 'v5/design-system/Input2';
import {
  AmountWithUnitInput,
  AmountWithUnitInputProps
} from 'v5/patterns/forms/fields/money/AmountWithUnitInput';
import {
  PercentInput,
  PercentInputProps
} from 'v5/patterns/forms/fields/money/PercentInput';
import {
  draftNumberOf,
  useDraftNumber
} from 'v5/patterns/forms/fields/money/useDraftNumber';
import { cn } from 'v5/platform/dom/cn';
import { Outlet } from 'v5/platform/react/outlet/Outlet';
import { OutletProvider } from 'v5/platform/react/outlet/OutletContext';
import { OutletInject } from 'v5/platform/react/outlet/OutletInject';
import { useFieldContext } from '../../FieldContext';
import { fieldAnchor } from '../../unitPath';
import { RateWithFees } from './RateWithFees';
import { RateCopy, useRateCopy } from './useRateCopy';

const labelClassName = 'text-sm font-semibold text-fg-default';

/**
 * The editor is a small form of its own: bordered fields read as fields and
 * keep the compound `CAD | 850 | / day` control legible, where the pane's
 * underline tone would dissolve it into three loose words.
 */
const EDITOR_TONE = 'regular';
const messageClassName = 'text-sm';

export function unitOptions(copy: RateCopy, current: RateInterval) {
  // An unsupported stored value (ITEM) stays visible so the error makes sense.
  const intervals: RateInterval[] = isResourceRateInterval(current)
    ? [...RESOURCE_RATE_INTERVALS]
    : [...RESOURCE_RATE_INTERVALS, current];
  return intervals.map(interval => ({
    value: interval,
    optionLabel: copy.unitOption(interval),
    shortLabel: copy.unitShort(interval),
    spokenLabel: copy.unitSpoken(interval)
  }));
}

export function toInterval(value: string): RateInterval | null {
  return (
    Object.values(RateInterval).find(interval => interval === value) ?? null
  );
}

type AmountControlProps = Omit<
  AmountWithUnitInputProps,
  'value' | 'onChange'
> & {
  amount: number | null;
  onAmountChange(amount: number | null): void;
  onAmountBlur(): void;
  locale: string;
};

/** `AmountWithUnitInput` with the amount kept as typed text (`useDraftNumber`). */
export const AmountControl = forwardRef<HTMLInputElement, AmountControlProps>(
  function AmountControl(
    { amount, onAmountChange, onAmountBlur, locale, currency, ...props },
    ref
  ) {
    const decimalSeparator = useMemo(
      () => decimalSeparatorOf(locale),
      [locale]
    );
    const draft = useDraftNumber({
      value: amount,
      onChange: onAmountChange,
      onBlur: onAmountBlur,
      format: value => formatMoneyAmount(value, locale),
      parse: text =>
        draftNumberOf(parseMoneyInput(text, { decimalSeparator, currency }))
    });
    return (
      <AmountWithUnitInput
        ref={ref}
        {...props}
        currency={currency}
        value={draft.value}
        onChange={draft.onChange}
        onBlur={draft.onBlur}
      />
    );
  }
);

type FeeControlProps = Omit<PercentInputProps, 'value' | 'onChange'> & {
  fee: number | null;
  onFeeChange(fee: number | null): void;
  onFeeBlur(): void;
  locale: string;
};

const FeeControl = forwardRef<HTMLInputElement, FeeControlProps>(
  function FeeControl({ fee, onFeeChange, onFeeBlur, locale, ...props }, ref) {
    const decimalSeparator = useMemo(
      () => decimalSeparatorOf(locale),
      [locale]
    );
    const draft = useDraftNumber({
      value: fee,
      onChange: onFeeChange,
      onBlur: onFeeBlur,
      format: value => String(value),
      parse: text =>
        draftNumberOf(
          parseMoneyInput(text.replace(/%\s*$/, ''), { decimalSeparator })
        )
    });
    return (
      <PercentInput
        ref={ref}
        placeholder="0"
        {...props}
        value={draft.value}
        onChange={draft.onChange}
        onBlur={draft.onBlur}
      />
    );
  }
);

function NameInput({
  path,
  followsUnit,
  onTyped
}: {
  path: string;
  followsUnit: boolean;
  onTyped(): void;
}) {
  const ctx = useFieldContext();
  const { t } = useTranslation('core');
  return (
    <FormField
      name={`${path}.name`}
      render={({ field }) => (
        <FormItem className="min-w-0 gap-1.5" {...fieldAnchor(field.name)}>
          <FormLabel className={labelClassName}>
            {t('rateEditor.name')}
          </FormLabel>
          <FormControl>
            <Input2
              tone={EDITOR_TONE}
              size={ctx.size}
              autoComplete="off"
              placeholder={t('rateEditor.namePlaceholder')}
              {...field}
              value={field.value ?? ''}
              onChange={event => {
                onTyped();
                field.onChange(event.target.value);
              }}
              onFocus={event => {
                // A generated name is selected, so typing replaces it.
                if (followsUnit) event.target.select();
              }}
            />
          </FormControl>
          <FormMessage className={messageClassName} />
        </FormItem>
      )}
    />
  );
}

function AmountInput({
  spec,
  path,
  onIntervalChange
}: {
  spec: Pick<FieldSpecOf<'rate'>, 'currencies'>;
  path: string;
  onIntervalChange(interval: RateInterval): void;
}) {
  const ctx = useFieldContext();
  const copy = useRateCopy();
  const { t } = useTranslation('core');
  const { getValues, trigger } = useFormContext();
  const currency = useController({ name: `${path}.currency` });
  const interval = useController({ name: `${path}.interval` });
  const currencies = useMemo(
    () =>
      spec.currencies.map(option => ({
        code: option.value,
        name: option.label
      })),
    [spec.currencies]
  );
  const units = useMemo(
    () => unitOptions(copy, interval.field.value),
    [copy, interval.field.value]
  );

  return (
    <FormField
      name={`${path}.amount`}
      render={({ field, fieldState }) => (
        <FormItem className="min-w-0 gap-1.5" {...fieldAnchor(field.name)}>
          <FormLabel className={labelClassName}>
            {t('rateEditor.amount')}
          </FormLabel>
          <FormControl>
            <AmountControl
              ref={field.ref}
              name={field.name}
              amount={field.value}
              onAmountChange={field.onChange}
              onAmountBlur={field.onBlur}
              locale={copy.locale}
              placeholder={t('rateEditor.amountPlaceholder')}
              currency={currency.field.value}
              currencies={currencies}
              onCurrencyChange={code => {
                currency.field.onChange(code);
                // Allowed decimals depend on the currency (JPY has none).
                if (getValues(`${path}.amount`) !== null) {
                  void trigger(`${path}.amount`);
                }
              }}
              unit={interval.field.value}
              units={units}
              onUnitChange={value => {
                const next = toInterval(value);
                if (next === null) return;
                interval.field.onChange(next);
                onIntervalChange(next);
              }}
              currencyLabel={t('rateEditor.currency')}
              unitLabel={t('rateEditor.billingUnit')}
              tone={EDITOR_TONE}
              size={ctx.size}
              invalid={
                fieldState.error !== undefined ||
                interval.fieldState.error !== undefined
              }
            />
          </FormControl>
          <FormMessage className={messageClassName} />
          {interval.fieldState.error?.message && (
            <p className="text-sm font-medium text-fg-danger">
              {interval.fieldState.error.message}
            </p>
          )}
        </FormItem>
      )}
    />
  );
}

function FeeInput({ path, fee }: { path: string; fee: RateFee }) {
  const ctx = useFieldContext();
  const copy = useRateCopy();
  return (
    <FormField
      name={`${path}.${fee.column}`}
      render={({ field, fieldState }) => (
        <FormItem className="min-w-0 gap-1" {...fieldAnchor(field.name)}>
          <FormLabel className="text-sm font-semibold text-fg-muted">
            {copy.feeShort(fee)}
          </FormLabel>
          <FormControl>
            <FeeControl
              ref={field.ref}
              name={field.name}
              fee={field.value}
              onFeeChange={field.onChange}
              onFeeBlur={field.onBlur}
              locale={copy.locale}
              aria-label={copy.feeFull(fee)}
              tone={EDITOR_TONE}
              size={ctx.size}
              invalid={fieldState.error !== undefined}
            />
          </FormControl>
          <OutletInject>
            <FormMessage className={messageClassName} />
          </OutletInject>
        </FormItem>
      )}
    />
  );
}

function FeesInput({ path, className }: { path: string; className: string }) {
  const { t } = useTranslation('core');
  return (
    <fieldset className={cn('min-w-0', className)}>
      <legend className={cn(labelClassName, 'mb-1.5')}>
        {t('rateEditor.fees')}{' '}
        <span className="font-normal text-fg-muted">
          {t('rateEditor.feesHint')}
        </span>
      </legend>
      <OutletProvider>
        <div className="grid grid-cols-[repeat(auto-fit,minmax(5rem,1fr))] gap-2 @min-[480px]:grid-cols-[repeat(3,minmax(5rem,8.5rem))]">
          {RATE_FEES.map(fee => (
            <FeeInput key={fee.column} path={path} fee={fee} />
          ))}
        </div>
        <Outlet className="mt-1.5 flex flex-col gap-1 empty:hidden" />
      </OutletProvider>
    </fieldset>
  );
}

function NoteInput({ path, className }: { path: string; className: string }) {
  const ctx = useFieldContext();
  const { t } = useTranslation('core');
  return (
    <FormField
      name={`${path}.description`}
      render={({ field }) => (
        <FormItem
          className={cn('min-w-0 gap-1.5', className)}
          {...fieldAnchor(field.name)}
        >
          <FormLabel className={labelClassName}>
            {t('rateEditor.note')}{' '}
            <span className="font-normal text-fg-muted">
              {t('rateEditor.optional')}
            </span>
          </FormLabel>
          <FormControl>
            <TextArea2
              tone={EDITOR_TONE}
              size={ctx.size}
              rows={1}
              // It grows with its content, so a manual resize handle only adds noise.
              className="resize-none"
              placeholder={t('rateEditor.notePlaceholder')}
              {...field}
              value={field.value ?? ''}
            />
          </FormControl>
        </FormItem>
      )}
    />
  );
}

/**
 * Edit mode of one custom rate. Reads top to bottom as the rate is spoken:
 * name, then `CAD | 850 | / day`, then fees in booking order, then a note.
 * Under 480px of list width everything stacks (side drawer); from 480px the
 * name and the amount share a row (wide pane, create dialog).
 *
 * A new rate is named after its billing unit ("Day rate") until someone
 * types a name, so a valid rate needs one field: the amount.
 */
export function RateEditor({
  spec,
  path
}: {
  spec: Pick<FieldSpecOf<'rate'>, 'currencies'>;
  path: string;
}) {
  const copy = useRateCopy();
  const { getValues, setValue, watch } = useFormContext();
  const [nameFollowsUnit, setNameFollowsUnit] = useState(() => {
    const value: RateFormValue = getValues(path);
    return value.name === '' || value.name === copy.autoName(value.interval);
  });
  const followsRef = useRef(nameFollowsUnit);
  followsRef.current = nameFollowsUnit;

  // Give an empty name the generated one while the name follows the unit:
  // on open, and again whenever the host resets the form. UnitForm resets
  // right after this editor mounts, in the same commit, so a render-driven
  // effect would never see the name go empty; a value subscription does.
  // Typing a name (or clearing it) stops the name from following.
  useEffect(() => {
    // setValue notifies this very subscription, synchronously. Until the
    // host's useForm has mounted (a form created together with this editor),
    // getValues still reads the default values, where the name stays empty:
    // without the flag the fill would call itself forever.
    let filling = false;
    const fillGeneratedName = () => {
      if (filling || !followsRef.current) return;
      // Undefined while the rate's row leaves the list (removed, or the
      // list reset on Cancel): watchers hear of it before it unmounts.
      const value: RateFormValue | undefined = getValues(path);
      if (value === undefined || value.name !== '') return;
      filling = true;
      try {
        // Not a user change: opening a new rate must not mark the form dirty.
        setValue(`${path}.name`, copy.autoName(value.interval), {
          shouldDirty: false
        });
      } finally {
        filling = false;
      }
    };
    fillGeneratedName();
    const subscription = watch(fillGeneratedName);
    return () => subscription.unsubscribe();
  }, [copy, getValues, path, setValue, watch]);

  return (
    <div data-testid="rate-editor" className="flex flex-col gap-3">
      <div className="grid grid-cols-1 gap-3 @min-[480px]:grid-cols-[minmax(0,1fr)_minmax(0,1.35fr)]">
        <NameInput
          path={path}
          followsUnit={nameFollowsUnit}
          onTyped={() => {
            // Synchronously: the name's own change notifies the subscription
            // above before React renders the new state.
            followsRef.current = false;
            setNameFollowsUnit(false);
          }}
        />
        <AmountInput
          spec={spec}
          path={path}
          onIntervalChange={next => {
            if (nameFollowsUnit) {
              setValue(`${path}.name`, copy.autoName(next), {
                shouldDirty: true,
                shouldValidate: true
              });
            }
          }}
        />
        <FeesInput path={path} className="@min-[480px]:col-span-2" />
        <NoteInput path={path} className="@min-[480px]:col-span-2" />
      </div>
      <RateWithFees path={path} />
    </div>
  );
}
